import Grid from '@mui/material/Grid';
import { useRollbar } from '@rollbar/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import Button, {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import LeadFormContainer from '@/components/Form/LeadFormContainer/LeadFormContainer';
import { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import {
  type LeadFormInputData,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import { splitFullName } from '@/utils/forms/splitFullName';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import { OwnHome } from '@/utils/validation/inputs';
import useStyles from './PaidLander.styles';
import { type PaidLanderProps, PaidLanderSteps } from './PaidLander.types';
import FailureStep from './Steps/FailureStep';
import QualifyStep from './Steps/QualifyStep';
import SubmitStep from './Steps/SubmitStep';
import SuccessStep from './Steps/SuccessStep';
import {
  QualifyStepSchemaMode,
  type QualifyStepSchemaType,
  SubmitStepSchemaMode,
  type SubmitStepSchemaType,
} from './schema';

const scrollElementIntoView = (element: HTMLElement | null) => {
  // Both the mobile and desktop navs render with id="global-nav" and are
  // toggled via CSS, so pick the one that is actually visible. The hidden nav
  // is display:none and therefore has no client rects.
  const globalNav = Array.from(
    document.querySelectorAll<HTMLElement>('#global-nav')
  ).find((nav) => nav.getClientRects().length > 0);
  if (!element) return;

  const elRect = element.getBoundingClientRect();
  // calculating element start position relative to document
  const elementTop = window.scrollY + elRect.top;

  if (globalNav) {
    const navRect = globalNav.getBoundingClientRect();

    // window scrolls up, navbar gets hidden
    if (elRect.top >= 0) {
      window.scrollTo({ top: elementTop, behavior: 'smooth' });
    } else {
      // window scrolls down, navbar always shows up
      window.scrollTo({
        top: elementTop - navRect.height,
        behavior: 'smooth',
      });
    }
  } else {
    // page uses minimal or no header
    window.scrollTo({ top: elementTop, behavior: 'smooth' });
  }
};

export default function PaidLander({ content }: PaidLanderProps) {
  const { fixedGrid, isOnDark, campaignId, overrideCampaignId, autoFocus } =
    content.fields;
  const rollbar = useRollbar();
  const { classes, cx } = useStyles({ isOnDark });
  const { submitLeadToZeus, trackFormStarted } = useSubmitLeadToZeus(
    'Interactive Quote Form'
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const [visibleStep, setVisibleStep] = useState(PaidLanderSteps.QUALIFY);

  // Focus the zip field on page load if autoFocus is enabled and this is the topmost paid lander
  useEffect(() => {
    if (!autoFocus) return;

    const allPaidLanders = document.querySelectorAll(
      '[data-component="paid-lander"]'
    );
    const isFirstPaidLander =
      allPaidLanders.length > 0 && allPaidLanders[0] === containerRef.current;

    if (isFirstPaidLander && zipInputRef.current) {
      zipInputRef.current.focus();
    }
  }, [autoFocus]);
  const [qualifyData, setQualifyData] = useState<QualifyStepSchemaType>();

  const handleNext = (data: QualifyStepSchemaType) => {
    scrollElementIntoView(containerRef.current);
    setQualifyData(data);

    if (
      data.mode === QualifyStepSchemaMode.ALL_FIELDS ||
      data.mode === QualifyStepSchemaMode.ZIP_AND_OWNHOME
    ) {
      if (data.ownHome !== OwnHome.OWN) {
        setVisibleStep(PaidLanderSteps.FAILURE);
        return;
      }
    }

    setVisibleStep(PaidLanderSteps.SUBMIT);
  };

  const handleBack = () => {
    setVisibleStep(PaidLanderSteps.QUALIFY);
  };

  const handleSubmitLanderForm = (data: SubmitStepSchemaType) => {
    setVisibleStep(PaidLanderSteps.SUCCESS);

    scrollElementIntoView(containerRef.current);

    if (qualifyData) {
      onSubmit(qualifyData, data);
    }
  };

  const onSubmit = async (
    firstStepData: QualifyStepSchemaType,
    secondStepData: SubmitStepSchemaType
  ) => {
    const { zipCode } = firstStepData;
    const { fullName, phone, address } = secondStepData;

    let email: string | undefined;
    let lastBill: string | undefined;
    let ownHome: OwnHome | undefined;

    if (firstStepData.mode === QualifyStepSchemaMode.ALL_FIELDS) {
      lastBill = firstStepData.lastBill;
      ownHome = firstStepData.ownHome;
    }
    if (firstStepData.mode === QualifyStepSchemaMode.ZIP_AND_BILL) {
      lastBill = firstStepData.lastBill;
    }
    if (firstStepData.mode === QualifyStepSchemaMode.ZIP_AND_OWNHOME) {
      ownHome = firstStepData.ownHome;
    }
    if (secondStepData.mode === SubmitStepSchemaMode.WITH_EMAIL) {
      email = secondStepData.email;
    }

    const { firstName, lastName } = splitFullName(fullName);

    try {
      const zeusLeadFormData: LeadFormInputData = {
        first_name: firstName,
        last_name: lastName,
        email,
        zip: zipCode,
        phone_home: phone,
        electric_bill: lastBill,
        property_ownership: ownHome,
      };

      await submitLeadToZeus({
        zeusData: zeusLeadFormData,
        contentfulCampaignId: campaignId,
        overrideCampaignId,
        honeypot: Boolean(address),
      });
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      return;
    }
  };

  return (
    <div
      className={cx(classes.container, {
        [classes.fullHeightContainer]: !fixedGrid,
      })}
      ref={containerRef}
      data-component='paid-lander'
    >
      <GridContainer fixed={fixedGrid}>
        <Grid>
          <Button
            iconName={IconName.ARROW_LEFT}
            iconPosition={ButtonIconPosition.LEFT}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SM}
            label='Previous step'
            onClick={handleBack}
            isOnDark={isOnDark}
            className={classNames({
              [classes.hiddenBackButton]:
                visibleStep !== PaidLanderSteps.SUBMIT,
            })}
          />
        </Grid>
      </GridContainer>
      <div className={classes.content}>
        <LeadFormContainer onFocusCapture={trackFormStarted}>
          <QualifyStep
            {...content.fields}
            nextPage={handleNext}
            isVisible={visibleStep === PaidLanderSteps.QUALIFY}
            zipInputRef={zipInputRef}
          />
          <SubmitStep
            {...content.fields}
            submit={handleSubmitLanderForm}
            isVisible={visibleStep === PaidLanderSteps.SUBMIT}
          />
        </LeadFormContainer>
        <SuccessStep
          {...content.fields}
          isVisible={visibleStep === PaidLanderSteps.SUCCESS}
        />
        <FailureStep
          {...content.fields}
          isVisible={visibleStep === PaidLanderSteps.FAILURE}
        />
      </div>
    </div>
  );
}
